import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ArrowOutwardIcon } from "@/assets/icons";
import type { ComponentType } from "react";

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.33, 1, 0.68, 1] as const },
  },
};

interface HeroSectionProps {
  video: {
    webm: string;
    mp4: string;
    poster: string;
  };
  headline: string;
  headlineClassName?: string;
  headlineItalic?: boolean;
  subtitle: React.ReactNode;
  subtitleMaxWidth?: string;
  primaryCta: {
    icon: ComponentType<{ className?: string }>;
    label: string;
    to: string;
    search?: Record<string, string>;
  };
  secondaryCta: {
    label: string;
    to: string;
  };
}

export default function HeroSection({
  video,
  headline,
  headlineClassName,
  headlineItalic,
  subtitle,
  subtitleMaxWidth = "600px",
  primaryCta,
  secondaryCta,
}: HeroSectionProps) {
  const PrimaryCTAIcon = primaryCta.icon;

  return (
    <section className="relative h-[min(820px,100svh)] md:h-[600px] lg:h-[760px] overflow-hidden">
      <video
        poster={video.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
      >
        <source src={video.webm} type="video/webm" />
        <source src={video.mp4} type="video/mp4" />
      </video>

      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(1,7,29,0.10) 0%, rgba(1,7,29,0.10) 25%, rgba(1,7,29,0.80) 55%, rgba(1,7,29,1) 85%)",
        }}
      />

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[80px] bg-[#01071d]" />

      <motion.div
        className="relative z-10 flex h-full items-end justify-center px-4 pb-20 md:px-8 md:pb-24 lg:px-12 lg:pb-28"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <div className="flex w-full max-w-[1400px] flex-col items-center gap-10 text-center lg:gap-[60px]">
          <div className="flex flex-col gap-2">
            <motion.h1
              data-testid="hero-heading"
              className={`font-figtree font-black uppercase leading-none text-white ${headlineClassName ?? "text-[48px] tracking-[-0.72px] md:text-[74px] md:tracking-[-1.11px] lg:text-[100px] lg:tracking-[-1.5px]"}${headlineItalic ? " italic" : ""}`}
              style={{ textShadow: "0px 3px 12px rgba(5, 12, 43, 0.35)" }}
              variants={fadeUp}
            >
              {headline}
            </motion.h1>
            <motion.p
              className="mx-auto font-open-sans text-[16px] font-normal leading-[1.4] text-white/90 md:text-[18px] lg:text-[20px]"
              style={{
                maxWidth: subtitleMaxWidth,
                textShadow: "0px 5px 8px rgba(5, 12, 43, 0.14)",
              }}
              variants={fadeUp}
            >
              {subtitle}
            </motion.p>
          </div>

          <motion.div
            className="flex flex-col items-center gap-2 sm:flex-row sm:gap-6"
            variants={fadeUp}
          >
            <Link
              to={primaryCta.to}
              search={primaryCta.search as never}
              resetScroll={false}
            >
              <div className="flex h-14 items-center justify-center gap-2 rounded-full border border-[#576ee8] bg-background-accent-primary px-7 shadow-[0px_0px_20px_0px_rgba(137,186,255,0.6)] transition-all hover:brightness-90 lg:h-16 lg:px-7">
                <PrimaryCTAIcon className="size-5 shrink-0 text-teal-500 lg:size-7" />
                <span className="whitespace-nowrap font-open-sans text-[16px] font-bold leading-[1.4] text-white lg:text-[18px]">
                  {primaryCta.label}
                </span>
              </div>
            </Link>
            <Link
              to={secondaryCta.to}
              className="flex h-12 items-center gap-1 text-white transition-colors hover:text-teal-500 lg:h-14"
            >
              <span className="whitespace-nowrap font-open-sans text-[16px] font-bold leading-[1.4] lg:text-[18px]">
                {secondaryCta.label}
              </span>
              <ArrowOutwardIcon className="size-6 shrink-0 lg:size-7" />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <svg
        className="pointer-events-none absolute bottom-0 left-0 w-full h-[60px]"
        viewBox="0 0 390 100"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: "drop-shadow(0px 0px 40px rgba(255, 255, 255, 0.50))",
        }}
      >
        <path
          d="M0 40C53.0762 40 130.446 73.9517 195.729 73.9517C260.726 73.9517 343.26 40 390 40V100H0V40Z"
          fill="#fafbfd"
        />
      </svg>
    </section>
  );
}
